import React, { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { ChartColumn, CircleCheck, Clock, Megaphone, MousePointerClick, ReceiptText, ShoppingCart } from "lucide-react";
import { StorefrontService } from "../../../core/services/storefront.service";
import { StatCard } from "./StatCard";

// Chart colours: the brand flow's tokens (modules/shared/theme/theme.scss).
const PRIMARY = "#66A9DD";
const BORDER = "#e2e8f0";
const FONT = "inherit";

interface Props {
  brand_id: number;
}

const STATUS_PILL: Record<string, string> = {
  approved: "brd-pill--success",
  pending: "brd-pill--warning",
  cancelled: "brd-pill--error",
  rejected: "brd-pill--error",
};

type GroupBy = "overall" | "daily" | "influencer" | "product" | "geo";

const DIMENSIONS: { key: GroupBy; label: string }[] = [
  { key: "overall", label: "Overall" },
  { key: "daily", label: "Daily" },
  { key: "influencer", label: "By Influencer" },
  { key: "product", label: "By Product" },
  { key: "geo", label: "By Geography" },
];

const DIMENSION_KEY: Record<GroupBy, string | null> = {
  overall: null,
  daily: "date",
  influencer: "influencer_name",
  product: "product_name",
  geo: "country",
};

const DIMENSION_HEADER: Record<GroupBy, string> = {
  overall: "",
  daily: "Date",
  influencer: "Influencer",
  product: "Product",
  geo: "Country",
};

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  const toStr = (d: Date) => d.toISOString().slice(0, 10);
  return { start: toStr(start), end: toStr(end) };
}

function buildChart(groupBy: GroupBy, performance: any[]): Highcharts.Options | null {
  if (performance.length === 0) return null;
  const dimKey = DIMENSION_KEY[groupBy];

  if (groupBy === "daily") {
    const sorted = [...performance].sort((a, b) => (a.date || "").toString().localeCompare((b.date || "").toString()));
    return {
      chart: { type: "areaspline", height: 260 },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: {
        categories: sorted.map((r) => (r.date ? new Date(r.date).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) : "")),
        lineColor: BORDER,
        labels: { style: { fontFamily: FONT, fontSize: "12px" } },
      },
      yAxis: { title: { text: "Clicks" }, allowDecimals: false, gridLineColor: "#f0f0f0", min: 0, labels: { style: { fontFamily: FONT } } },
      legend: { enabled: false },
      tooltip: { pointFormat: "<b>{point.y}</b> clicks" },
      plotOptions: {
        areaspline: {
          color: PRIMARY,
          lineWidth: 3,
          marker: { fillColor: PRIMARY, radius: 4 },
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, `${PRIMARY}66`],
              [1, `${PRIMARY}00`],
            ],
          },
        },
      },
      series: [{ type: "areaspline", name: "Clicks", data: sorted.map((r) => r.clicks) }],
    };
  }

  if (!dimKey) return null;
  const labeled = performance
    .map((r) => ({ label: r[dimKey] || "Unknown", clicks: r.clicks }))
    .sort((a, b) => b.clicks - a.clicks)
    .slice(0, 8);
  if (labeled.every((r) => r.clicks === 0)) return null;

  return {
    chart: { type: "column", height: 260 },
    title: { text: undefined },
    credits: { enabled: false },
    xAxis: { categories: labeled.map((r) => r.label), lineColor: BORDER, labels: { style: { fontFamily: FONT, fontSize: "12px" } } },
    yAxis: { title: { text: "Clicks" }, allowDecimals: false, gridLineColor: "#f0f0f0", min: 0, labels: { style: { fontFamily: FONT } } },
    legend: { enabled: false },
    tooltip: { pointFormat: "<b>{point.y}</b> clicks" },
    plotOptions: { column: { color: PRIMARY, borderRadius: 4, pointPadding: 0.15, borderWidth: 0 } },
    series: [{ type: "column", name: "Clicks", data: labeled.map((r) => r.clicks) }],
  };
}

// Brand's own scoped view of their linked CashKaro Affiliate Deals campaign's
// Trackier performance — clicks/payout trend, which influencers are
// promoting them, which products convert. Reads /storefront/cashkaro-performance
// (Storefront.getCashkaroPerformance), scoped server-side by this brand's
// own cctm_id — never sees other brands' data.
export const StorefrontCashkaroPerformance: React.FC<Props> = ({ brand_id }) => {
  const [range, setRange] = useState(defaultRange());
  const [groupBy, setGroupBy] = useState<GroupBy>("influencer");
  const [performance, setPerformance] = useState<any[]>([]);
  const [conversions, setConversions] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [noStorefront, setNoStorefront] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!brand_id) return;
    setLoading(true);
    StorefrontService.getCashkaroPerformance({ brand_id, start: range.start, end: range.end, group_by: groupBy })
      .then((res: any) => {
        if (res?.data?.no_storefront) {
          setNoStorefront(true);
          setPerformance([]);
          setConversions([]);
          setSummary(null);
          return;
        }
        setNoStorefront(false);
        setPerformance(Array.isArray(res?.data?.performance) ? res.data.performance : []);
        setConversions(Array.isArray(res?.data?.conversions) ? res.data.conversions : []);
        setSummary(res?.data?.summary || null);
      })
      .catch(() => {
        setPerformance([]);
        setConversions([]);
        setSummary(null);
      })
      .finally(() => setLoading(false));
  }, [brand_id, range.start, range.end, groupBy]);

  const chartOptions = useMemo(() => buildChart(groupBy, performance), [groupBy, performance]);
  const dimKey = DIMENSION_KEY[groupBy];
  const dimHeader = DIMENSION_HEADER[groupBy];

  if (noStorefront) {
    return (
      <div className="brd-empty">
        <span className="brd-empty__icon"><Megaphone size={28} /></span>
        <p className="brd-empty__body">Set up your storefront and link an Affiliate Deals campaign first to see performance here.</p>
      </div>
    );
  }

  return (
    <div>
      <p className="brd-store__caption">How your linked Affiliate Deals campaign is performing on Trackier — clicks, conversions, and who's promoting you.</p>

      <div className="brd-store-dates">
        <label>From<input type="date" className="brd-input" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} /></label>
        <label>To<input type="date" className="brd-input" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} /></label>
      </div>
      <div className="brd-chip-scroll brd-store-filterchips" role="tablist" aria-label="Group by">
        {DIMENSIONS.map((d) => (
          <button key={d.key} type="button" role="tab" aria-selected={groupBy === d.key}
            className={`brd-chip${groupBy === d.key ? " brd-chip--on" : ""}`} onClick={() => setGroupBy(d.key)}>
            {d.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="brd-center"><span className="brd-spinner" /></div>
      ) : (
        <>
          <div className="brd-store-stats">
            <StatCard label="Clicks" value={String(summary?.clicks ?? 0)} icon={<MousePointerClick size={18} />} tone="primary" />
            <StatCard label="Approved Payout" value={`₹${Number(summary?.approved_payout || 0).toFixed(2)}`} icon={<CircleCheck size={18} />} tone="success" />
            <StatCard label="Pending Payout" value={`₹${Number(summary?.pending_payout || 0).toFixed(2)}`} icon={<Clock size={18} />} tone="warning" />
            <StatCard label="Total Conversions" value={String(summary?.total_conversions ?? 0)} icon={<ShoppingCart size={18} />} tone="violet" />
          </div>

          <section className="brd-section" style={{ padding: 0, overflow: "hidden" }}>
            <h3 className="brd-section__title brd-store-panel-title">
              <ChartColumn size={18} /> {groupBy === "daily" ? "Clicks trend" : `Performance — ${DIMENSIONS.find((d) => d.key === groupBy)?.label}`}
            </h3>
            {chartOptions ? (
              <div style={{ padding: 16 }}>
                <HighchartsReact highcharts={Highcharts} options={chartOptions} />
              </div>
            ) : (
              <p className="brd-store-dtable__empty">No clicks in this date range yet.</p>
            )}

            {dimKey && (
              <div className="brd-store-dtable brd-store-dtable--flush" style={{ borderTop: chartOptions ? `1px solid ${BORDER}` : "none" }}>
                <table>
                  <thead>
                    <tr>
                      <th>{dimHeader}</th>
                      <th className="brd-store-dtable__num">Clicks</th>
                      <th className="brd-store-dtable__num">Approved Conversions</th>
                      <th className="brd-store-dtable__num">Payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {performance.length === 0 ? (
                      <tr><td colSpan={4} className="brd-store-dtable__empty">No data in this date range.</td></tr>
                    ) : (
                      performance.map((r, i) => (
                        <tr key={i}>
                          <td><strong>{dimKey === "date" && r.date ? new Date(r.date).toLocaleDateString() : r[dimKey] || "—"}</strong></td>
                          <td className="brd-store-dtable__num">{r.clicks}</td>
                          <td className="brd-store-dtable__num">{r.approved_conversions}</td>
                          <td className="brd-store-dtable__num">₹{Number(r.payout || 0).toFixed(2)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="brd-section" style={{ padding: 0, overflow: "hidden" }}>
            <h3 className="brd-section__title brd-store-panel-title"><ReceiptText size={18} /> Conversion log</h3>
            {conversions.length === 0 ? (
              <p className="brd-store-dtable__empty">No conversions in this date range yet.</p>
            ) : (
              <div className="brd-store-dtable brd-store-dtable--flush">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Influencer</th>
                      <th>Product</th>
                      <th>Goal</th>
                      <th>Status</th>
                      <th className="brd-store-dtable__num">Payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conversions.map((c) => (
                      <tr key={c.trackier_conversion_id}>
                        <td className="brd-store-table__muted brd-store-dtable__nowrap">{c.trackier_created_at ? new Date(c.trackier_created_at).toLocaleDateString() : "—"}</td>
                        <td><strong>{c.influencer_name}</strong></td>
                        <td className="brd-store-table__muted">{c.product_name || "—"}</td>
                        <td className="brd-store-table__muted">{c.goal_name || "—"}</td>
                        <td><span className={`brd-pill ${STATUS_PILL[c.status] || "brd-pill--muted"}`} style={{ textTransform: "capitalize" }}>{c.status}</span></td>
                        <td className="brd-store-dtable__num">{c.currency || ""} {Number(c.payout || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default StorefrontCashkaroPerformance;
